/**
 * Actor-conditional `userShareD6` updater for `useGetMarketBackerEarnings`
 * receiving a per-market `morfi.market.${m}.user.*.backing.v0` event.
 *
 * The subject wildcards on actor, so EVERY user's back/unback hits this
 * recipe on every viewer's cache. The market-level `supplyD6` is updated
 * unconditionally (every backing changes total supply — declarative `add:`
 * recipe handles that). But `userShareD6` is per-user — only the connected
 * user's own backing should mutate their cached row. This helper bails
 * unless the event's `actor` matches the row's `userWallet`.
 *
 * Returns `prev` unchanged when:
 *   - The event is from another actor (cross-user pollution would corrupt
 *     this viewer's apparent share)
 *   - The connected user is anonymous (`userWallet` is null)
 *   - The signed delta is missing or unparseable
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

export interface BackerEarningsRowLike {
  readonly userWallet?: string | null;
  readonly userShareD6?: string;
  readonly backedD6?: string;
  readonly claimedTotalD6?: string;
}

function safeBigInt(value: unknown): bigint | null {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'string') {
    if (!/^-?\d+$/.test(value.trim())) return null;
    try {
      return BigInt(value.trim());
    } catch {
      return null;
    }
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return BigInt(Math.trunc(value));
  }
  return null;
}

export function applyBackingToBackerEarnings<T extends BackerEarningsRowLike>(
  prev: T,
  raw: Record<string, unknown>,
  args: Record<string, unknown> = {},
): T {
  if (typeof raw.actor !== 'string' || !isViewerWallet(prev, args, raw.actor)) return prev;
  const delta = safeBigInt(raw.sharesDeltaD6);
  if (delta === null || delta === 0n) return prev;
  const current = safeBigInt(prev.userShareD6 ?? '0') ?? 0n;
  const next = current + delta;
  const clamped = next < 0n ? 0n : next;
  if (clamped.toString() === (prev.userShareD6 ?? '0')) return prev;
  // Rows that carry the backing principal (the profile rollup) move it in
  // lockstep: backing and unbacking change principal, only claims don't.
  if (prev.backedD6 === undefined) return { ...prev, userShareD6: clamped.toString() };
  const backed = (safeBigInt(prev.backedD6) ?? 0n) + delta;
  return {
    ...prev,
    userShareD6: clamped.toString(),
    backedD6: (backed < 0n ? 0n : backed).toString(),
  };
}

export function isViewerWallet(
  prev: BackerEarningsRowLike,
  args: Record<string, unknown>,
  wallet: string,
): boolean {
  const walletCsv = args.walletPubkeysCsv;
  if (typeof walletCsv === 'string' && walletCsv.length > 0) {
    return walletCsv.split(',').includes(wallet);
  }
  return prev.userWallet !== undefined && prev.userWallet !== null && prev.userWallet === wallet;
}
